import React, { FC, memo } from 'react';
import ChevronDown from '$icons/chevron-down.svg';
import styles from './collapse.module.css';
import clsx from 'clsx';
import { Typography, TypographyProps } from '~/shared/components';

type SharedCollapseProps = {
    isOpen?: boolean;
    desktopOnly?: boolean;
    mobileOnly?: boolean;
};

type IconProps = SharedCollapseProps & {
    size?: 'small' | 'medium' | 'large';
};

const CollapseIcon: FC<IconProps> = memo(({ isOpen, size = 'medium' }) => {
    return (
        <ChevronDown
            data-collapse-icon
            className={clsx(isOpen ? styles.iconOpen : styles.iconClosed, styles[`${size}Size`])}
        />
    );
});

CollapseIcon.displayName = 'CollapseIcon';

type HeaderProps = SharedCollapseProps &
    React.HTMLAttributes<HTMLButtonElement> & {
        children?: React.ReactNode;
        as?: string;
        textVariant?: TypographyProps<'div'>['variant'];
    };

const CollapseHeader: FC<HeaderProps> = memo((props) => {
    const {
        children,
        className,
        textVariant = 'display5',
        as = 'div',
        isOpen,
        desktopOnly,
        mobileOnly,
        ...rest
    } = props;

    return (
        <button
            className={clsx(styles.trigger, className, {
                [styles.desktopOnly]: desktopOnly,
                [styles.mobileOnly]: mobileOnly,
            })}
            {...rest}
        >
            <Typography as={as} variant={textVariant} className={styles.triggerText}>
                {children}
            </Typography>
            <CollapseIcon isOpen={isOpen} />
        </button>
    );
});

CollapseHeader.displayName = 'CollapseHeader';

type PanelProps = SharedCollapseProps & React.HTMLAttributes<HTMLDivElement>;
const CollapsePanel: FC<PanelProps> = memo((props) => {
    const { children, className, isOpen, desktopOnly, mobileOnly, ...rest } = props;

    return (
        <div
            className={clsx(styles.panel, className, {
                [styles.isOpen]: isOpen,
                [styles.desktopOnly]: desktopOnly,
                [styles.mobileOnly]: mobileOnly,
            })}
            aria-hidden={!isOpen}
            role="region"
            {...rest}
        >
            <div>{children}</div>
        </div>
    );
});

CollapsePanel.displayName = 'CollapsePanel';
export { CollapsePanel, CollapseHeader, CollapseIcon };
